import { useMemo, useRef, useState } from 'react';

import { extractEventHandlers } from '../../../../../utils/dom';
import useControlledValue from '../../../../../utils/hooks/use-controlled-value';
import useForkRefs from '../../../../../utils/hooks/use-fork-refs';
import useIsomorphicLayoutEffect from '../../../../../utils/hooks/use-isomorphic-layout-effect';
import {
  asc,
  AXIS_PROPS,
  buildMarks,
  clamp,
  createChangeEvent,
  identity,
  toPercent,
  validateSliderRange,
} from '../utils';

import useSliderHiddenInputProps from './use-slider-hidden-input-props';
import useSliderPointerInteraction from './use-slider-pointer-interaction';

import type * as React from 'react';

import type { EventHandlers } from '../../../../../utils/dom';
import type {
  Axis,
  EmptyProps,
  GetRootProps,
  GetThumbProps,
  SliderValue,
  UseSliderBaseParameters,
  UseSliderBaseReturnValue,
  UseSliderRootSlotProps,
  UseSliderThumbSlotProps,
} from '../types';

/**
 * Core slider hook that manages all fundamental slider behavior:
 * - **Controlled/uncontrolled state** via `useControlledValue`.
 * - **Pointer interactions** (mouse/touch drag) via `useSliderPointerInteraction`.
 * - **Keyboard navigation** via `useSliderHiddenInputProps`.
 * - **Track position** calculation (`trackOffset` and `trackLeap`).
 * - **Mark generation** from the `marks` prop.
 * - **Thumb focus and hover** state tracking.
 *
 * This hook does NOT handle snap-to-marks, center-track, or number input logic —
 * those are layered on top by `useSlider`.
 *
 * @param parameters - Base slider configuration.
 * @returns State values, prop getter functions, and refs for building the slider UI.
 */
const useSliderBase = (parameters: UseSliderBaseParameters): UseSliderBaseReturnValue => {
  const {
    'aria-labelledby': ariaLabelledby,
    defaultValue,
    disabled = false,
    disableSwap = false,
    isRtl = false,
    marks: marksProp = false,
    max = 100,
    min = 0,
    minDistance = 0,
    name,
    onChange,
    onChangeCommitted,
    orientation = 'horizontal',
    readOnly = false,
    resolveKeyboardValue,
    rootRef: ref,
    scale = identity,
    shiftStep = 10,
    step = 1,
    tabIndex,
    value: valueProp,
  } = parameters;

  validateSliderRange(min, max);

  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(-1);
  const [dragging, setDragging] = useState(false);
  const [focusedThumbIndex, setFocusedThumbIndex] = useState(-1);
  const [valueDerived, setValueState] = useControlledValue<SliderValue>({
    controlledValue: valueProp,
    defaultValue: defaultValue ?? min,
    name: 'Slider',
  });

  // Detect range slider mode based on whether the value is an array
  const range = Array.isArray(valueDerived);
  // Normalize values into a sorted, clamped array (always an array internally,
  // even for single-value sliders, to simplify thumb rendering logic)
  const values = useMemo(() => {
    const nextValues = Array.isArray(valueDerived) ? [...valueDerived].toSorted(asc) : [valueDerived];

    if (nextValues.length === 0) {
      return [min];
    }

    return nextValues.map((value) =>
      typeof value === 'number' && Number.isFinite(value) ? clamp(value, min, max) : min,
    );
  }, [max, min, valueDerived]);

  const marks = useMemo(() => buildMarks(marksProp, min, max, step), [marksProp, max, min, step]);
  // Interaction logic only needs numeric positions, so collapse duplicate-value marks
  // while preserving the sorted order produced by `buildMarks()`.
  const marksValues = useMemo(() => [...new Set(marks.map((mark) => mark.value))], [marks]);
  const sliderRef = useRef<HTMLSpanElement>(null);
  const handleRef = useForkRefs(ref, sliderRef);
  // In RTL mode, the horizontal axis is reversed so CSS offsets use `right` instead of `left`
  const axis = (isRtl && orientation === 'horizontal' ? `${orientation}-reverse` : orientation) as Axis;

  const handleChange = useMemo(() => {
    if (!onChange) {
      return;
    }

    return (event: Event | React.SyntheticEvent, value: SliderValue, thumbIndex: number) => {
      onChange(createChangeEvent(event, value, name), value, thumbIndex);
    };
  }, [name, onChange]);

  const { createHandleMouseDown } = useSliderPointerInteraction({
    axis,
    disabled,
    disableSwap,
    handleChange,
    marks,
    marksValues,
    max,
    min,
    minDistance,
    onChangeCommitted,
    range,
    readOnly,
    setActive,
    setDragging,
    setOpen,
    setValueState,
    sliderRef,
    step,
    valueDerived: valueDerived ?? min,
    values,
  });

  const getHiddenInputProps = useSliderHiddenInputProps({
    ariaLabelledby,
    disabled,
    disableSwap,
    handleChange,
    isRtl,
    marks,
    marksValues,
    max,
    min,
    minDistance,
    name,
    onChangeCommitted,
    orientation,
    range,
    readOnly,
    resolveKeyboardValue,
    scale,
    setFocusedThumbIndex,
    setOpen,
    setValueState,
    shiftStep,
    sliderRef,
    step,
    tabIndex,
    valueDerived: valueDerived ?? min,
    values,
  });

  // Reset interaction states when the slider becomes disabled.
  // Uses the "adjusting state during rendering" pattern recommended by React
  // to avoid setting state inside useEffect.
  const [prevDisabled, setPrevDisabled] = useState(disabled);
  if (disabled !== prevDisabled) {
    setPrevDisabled(disabled);
    if (disabled) {
      setActive(-1);
      setOpen(-1);
      setDragging(false);
      setFocusedThumbIndex(-1);
    }
  }

  // Blur the focused thumb when disabled (DOM side effect).
  useIsomorphicLayoutEffect(() => {
    if (!disabled) {
      return;
    }

    if (sliderRef.current?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement | null)?.blur();
    }
  }, [disabled]);

  const getRootProps: GetRootProps = <ExternalProps extends Record<string, unknown> = EmptyProps>(
    externalProps: ExternalProps = {} as ExternalProps,
  ): UseSliderRootSlotProps<ExternalProps> => {
    const externalHandlers = extractEventHandlers(externalProps);

    return {
      ...externalProps,
      onMouseDown: createHandleMouseDown(externalHandlers),
      ref: handleRef,
    };
  };

  const createHandleMouseOver = (otherHandlers: EventHandlers) => (event: React.MouseEvent<HTMLSpanElement>) => {
    otherHandlers.onMouseOver?.(event);
    setOpen(Number(event.currentTarget.dataset.index));
  };

  const createHandleMouseLeave = (otherHandlers: EventHandlers) => (event: React.MouseEvent<HTMLSpanElement>) => {
    otherHandlers.onMouseLeave?.(event);
    setOpen(-1);
  };

  const getThumbProps: GetThumbProps = <ExternalProps extends Record<string, unknown> = EmptyProps>(
    externalProps: ExternalProps = {} as ExternalProps,
  ): UseSliderThumbSlotProps<ExternalProps> => {
    const externalHandlers = extractEventHandlers(externalProps);

    return {
      ...externalProps,
      ...externalHandlers,
      onMouseLeave: createHandleMouseLeave(externalHandlers),
      onMouseOver: createHandleMouseOver(externalHandlers),
    };
  };

  // Disable pointer events on inactive thumbs during drag to prevent the
  // non-dragged thumb from stealing mouse events.
  const getThumbStyle = (index: number): React.CSSProperties => ({
    pointerEvents: active !== -1 && active !== index ? 'none' : undefined,
  });

  // For range sliders, the track starts at the lower thumb; for single sliders, from min.
  const trackOffset = toPercent(range ? values[0] : min, min, max);
  const trackLeap = toPercent(Math.max(...values), min, max) - trackOffset;

  return {
    active,
    axis,
    axisProps: AXIS_PROPS,
    dragging,
    focusedThumbIndex,
    getHiddenInputProps,
    getRootProps,
    getThumbProps,
    getThumbStyle,
    marks,
    open,
    range,
    rootRef: handleRef,
    setValueState,
    trackLeap,
    trackOffset,
    values,
  };
};

export default useSliderBase;
